import { useState } from 'react';
import { toast } from 'sonner';
import { 
  ArrowRight,
  User,
  Heart,
  Smartphone,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { readPendingPlan, clearPendingPlan, CHECKOUT_FIRST_PLANS } from '../../lib/pendingPlan';
import { trackLead } from '../../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo Component
const Logo = ({ className = "h-14" }) => (
  <img 
    src="/logo-curacheck.png" 
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

const SetupWizard = ({ user, famiglia, onComplete, supabase, isEditMode = false, onCancel = null, onClose = null }) => {
  const [saving, setSaving] = useState(false);
  
  // Form data - semplificato (no più WhatsApp)
  const [formData, setFormData] = useState({
    nomeAssistito: famiglia?.nome_assistito || '',
    nomeAssistente: famiglia?.nome_assistente || ''
  });

  // VALIDAZIONE SEMPLICE (solo nome assistito e assistente)
  const isValid = () => {
    if (!formData.nomeAssistito.trim()) return false;
    if (!formData.nomeAssistente.trim()) return false;
    return true;
  };

  // SALVA E VAI DIRETTAMENTE ALLA DASHBOARD
  const handleSubmit = async () => {
    if (!isValid()) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    
    try {
      // Usa sempre l'API backend per evitare problemi RLS
      console.log('[SetupWizard] Saving via API...', { user_id: user?.id, famiglia_id: famiglia?.id });
      
      if (!user?.id) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        setSaving(false);
        return;
      }
      
      let response;
      const pending = readPendingPlan();
      try {
        const { data: { session } } = await supabase.auth.getSession();
        response = await fetch(`${API_URL}/api/register/trial`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
          },
          body: JSON.stringify({
            nome_famiglia: `Famiglia ${formData.nomeAssistito.split(' ').pop() || 'CuraCheck'}`,
            nome_assistito: formData.nomeAssistito,
            nome_assistente: formData.nomeAssistente,
            plan: pending?.plan || null
          })
        });
      } catch (networkError) {
        console.error('[SetupWizard] Network error:', networkError);
        toast.error('Errore di connessione. Verifica la tua connessione internet.');
        setSaving(false);
        return;
      }
      
      const result = await response.json();
      console.log('[SetupWizard] API response:', result);
      
      if (!response.ok) {
        throw new Error(result.detail || result.message || 'Errore nel salvataggio');
      }
      
      clearPendingPlan();
      // Meta Pixel: Lead solo quando nasce davvero una nuova famiglia in prova di 7 giorni
      let leadSent = false;
      if (result.piano === 'trial' && !result.updated && !result.checkout_required) {
        leadSent = trackLead(result.famiglia_id);
      }
      if (result.checkout_required && CHECKOUT_FIRST_PLANS.includes(pending?.plan)) {
        // Premium / Founder Club: nessuna prova, subito al pagamento Stripe
        const checkoutRes = await fetch(`${API_URL}/api/checkout/create-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plan: pending.plan,
            billing_period: pending.billingPeriod || 'monthly',
            origin_url: window.location.origin,
            user_id: user.id,
            email: user.email
          })
        });
        const checkoutData = await checkoutRes.json().catch(() => ({}));
        if (checkoutData.checkout_url) {
          window.location.href = checkoutData.checkout_url;
          return;
        }
        toast.error("Errore nell'avvio del pagamento. Puoi completarlo dalla dashboard.");
      }
      
      toast.success(result.updated ? 'Configurazione aggiornata!' : 'Famiglia creata!');
      // breve attesa perché l'evento Lead parta prima del ricaricamento della pagina
      if (leadSent) await new Promise((r) => setTimeout(r, 800));
      window.location.href = '/dashboard';
      
    } catch (error) {
      console.error('Setup error:', error);
      toast.error(error.message || 'Errore nel salvataggio. Riprova.');
      setSaving(false);
    }
  };

  // FORM PRINCIPALE - Semplificato (solo nome assistito e assistente)
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-[#E5E7EB]">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <Logo className="h-10" />
          <span className="text-sm text-[#64748B]">Configurazione rapida</span>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-6 relative">
          
          <div className="text-center mb-4">
            <h1 className="text-2xl font-bold text-[#2C3E50]">Configura CuraCheck</h1>
            <p className="text-[#64748B] text-sm mt-1">Compila i dati per iniziare</p>
          </div>

          {/* Pulsante Chiudi/Indietro (se onClose disponibile) */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}

          {/* Nome Assistito */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#2C3E50] mb-2">
              <Heart className="w-4 h-4 text-[#4A7C59]" />
              Chi assisti? *
            </label>
            <Input
              value={formData.nomeAssistito}
              onChange={(e) => setFormData({ ...formData, nomeAssistito: e.target.value })}
              placeholder="Es. Nonna Maria, Papà Giovanni..."
              className="h-12 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4A7C59]"
            />
          </div>

          {/* Nome Assistente */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#2C3E50] mb-2">
              <User className="w-4 h-4 text-[#4A7C59]" />
              Nome dell&apos;assistente/badante *
            </label>
            <Input
              value={formData.nomeAssistente}
              onChange={(e) => setFormData({ ...formData, nomeAssistente: e.target.value })}
              placeholder="Es. Anna, Maria..."
              className="h-12 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4A7C59]"
            />
          </div>

          {/* Info notifiche push */}
          <div className="bg-[#EBF5FB] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-[#5DADE2] mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#2471A3]">Notifiche Push</p>
                <p className="text-xs text-[#5DADE2] mt-1">
                  Dopo la configurazione, potrai attivare le notifiche push dalla Dashboard per ricevere avvisi in tempo reale sul tuo smartphone.
                </p>
              </div>
            </div>
          </div>

          {/* Bottone Submit */}
          <Button
            onClick={handleSubmit}
            disabled={saving || !isValid()}
            className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg disabled:opacity-50"
          >
            {saving ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Salvataggio...
              </>
            ) : (
              <>
                Inizia
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          <p className="text-center text-xs text-[#94A3B8]">
            I farmaci potrai aggiungerli dalla Dashboard
          </p>

        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
